/**
 * Image service for bottle image upload/delete operations
 */

import { supabase } from './supabase';

const BUCKET_NAME = 'bottle-images';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Validates an image file
 * @param {File} file - File to validate
 * @returns {{ valid: boolean, error?: string }}
 */
function validateImageFile(file) {
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    return { valid: false, error: 'Invalid file type. Only JPEG, PNG, and WebP are allowed.' };
  }

  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: 'File too large. Maximum size is 5MB.' };
  }

  return { valid: true };
}

/**
 * Gets the file extension from a file object
 * @param {File} file - File object
 * @returns {string} File extension
 */
function getFileExtension(file) {
  const mimeToExt = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp'
  };
  return mimeToExt[file.type] || 'jpg';
}

/**
 * Extracts the storage path from a public URL
 * @param {string} imageUrl - Full public URL of the image
 * @returns {string|null} Storage path or null if invalid
 */
function getPathFromUrl(imageUrl) {
  if (!imageUrl) return null;

  try {
    const url = new URL(imageUrl);
    // URL format: .../storage/v1/object/public/bottle-images/{path}
    const match = url.pathname.match(/\/storage\/v1\/object\/public\/bottle-images\/(.+)/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

/**
 * Image service object with all storage operations
 */
export const imageService = {
  /**
   * Uploads a bottle image to storage
   * @param {File} file - Image file to upload
   * @param {string} bottleId - Bottle UUID
   * @param {'front'|'back'} side - Which side of the bottle
   * @returns {Promise<string>} Public URL of uploaded image
   */
  async uploadBottleImage(file, bottleId, side) {
    const validation = validateImageFile(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const ext = getFileExtension(file);
    const path = `${user.id}/${bottleId}_${side}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(path, file, {
        upsert: true,
        contentType: file.type
      });

    if (uploadError) {
      throw uploadError;
    }

    const { data: { publicUrl } } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(path);

    return publicUrl;
  },

  /**
   * Deletes a bottle image from storage
   * @param {string} imageUrl - Public URL of the image to delete
   * @returns {Promise<void>}
   */
  async deleteBottleImage(imageUrl) {
    const path = getPathFromUrl(imageUrl);
    if (!path) {
      return; // Nothing to delete
    }

    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .remove([path]);

    if (error) {
      throw error;
    }
  },

  /**
   * Validates an image file without uploading
   * @param {File} file - File to validate
   * @returns {{ valid: boolean, error?: string }}
   */
  validateImage: validateImageFile
};
